import { defineStore } from 'pinia'

export const useBreadcrumbStore = defineStore('breadcrumb', {
  state: () => ({
    /**
     * @type {boolean} Determines if breadcrumb should be displayed
     */
    displayBreadcrumb: true,

    /**
     * @type {Array} Breadcrumb items for the current module
     */
    breadcrumbs: [],

    /**
     * @type {Array} Action buttons to display on the right side of the breadcrumb
     * @property {string} label - Button label
     * @property {string} [icon] - Button icon
     * @property {Function} [action] - Click handler
     * @property {string} [severity] - Button severity (e.g., 'primary', 'secondary')
     * @property {boolean} [outlined] - Whether button is outlined
     */
    actions: [],
  }),

  actions: {
    /**
     * Toggles the breadcrumb state of the main menu.
     *
     * @param {boolean} state - Whether to display the breadcrumb
     */
    setBreadcrumbState(state) {
      this.displayBreadcrumb = Boolean(state)
    },

    /**
     * Updates the breadcrumb items for the current module.
     *
     * @param {Array|null} breadcrumbs - Breadcrumb items or null to hide breadcrumbs
     * @param {object} breadcrumbs[].label - Label for the breadcrumb (will be translated)
     * @param {string} [breadcrumbs[].route] - (Optional) Route to navigate
     * @param {string} [breadcrumbs[].icon] - (Optional) Icon class to display
     */
    setBreadcrumb(breadcrumbs) {
      // Handle null or empty breadcrumbs
      if (
        !breadcrumbs
        || (Array.isArray(breadcrumbs) && breadcrumbs.length === 0)
      ) {
        this.displayBreadcrumb = false
        this.breadcrumbs = []
        this.actions = []
        return
      }

      // Input validation
      if (!Array.isArray(breadcrumbs)) {
        console.error('Breadcrumbs must be an array:', breadcrumbs)
        this.displayBreadcrumb = false
        this.breadcrumbs = []
        this.actions = []
        return
      }

      // Validate each breadcrumb item
      const validBreadcrumbs = breadcrumbs.filter((breadcrumb) => {
        if (!breadcrumb || typeof breadcrumb !== 'object') {
          console.warn('Invalid breadcrumb item (not an object):', breadcrumb)
          return false
        }

        if (!breadcrumb.label || typeof breadcrumb.label !== 'string') {
          console.warn(`Invalid breadcrumb label:`, breadcrumb)
          return false
        }

        // Route validation (if present)
        if (
          breadcrumb.route !== undefined
          && typeof breadcrumb.route !== 'string'
          && typeof breadcrumb.route !== 'object'
        ) {
          console.warn(`Invalid breadcrumb route:`, breadcrumb.route)
          breadcrumb.route = null
        }

        return true
      })

      this.displayBreadcrumb = validBreadcrumbs.length > 0
      this.breadcrumbs = validBreadcrumbs
      this.actions = []
    },

    /**
     * Sets the action buttons for the breadcrumb
     * @param {Array} actions - Array of action objects
     */
    setActions(actions) {
      if (!Array.isArray(actions)) {
        this.actions = []
        return
      }
      this.actions = actions
    },

    /**
     * Clears all breadcrumbs and hides the breadcrumb component
     */
    clearBreadcrumbs() {
      this.displayBreadcrumb = false
      this.breadcrumbs = []
      this.actions = []
    },
  },

  getters: {
    /**
     * Checks if breadcrumbs are available and should be displayed
     * @returns {boolean} Whether breadcrumbs are available to display
     */
    hasBreadcrumbs() {
      return (
        this.displayBreadcrumb
        && Array.isArray(this.breadcrumbs)
        && this.breadcrumbs.length > 0
      )
    },
  },
})
